import { useMutation, useQueryClient } from '@tanstack/react-query';

import { myProfileApi } from '../api/my-profile.adapter';
import type {
  MyProfile,
  PasswordChangeInput,
  PhoneSaveInput,
  PhoneSendCodeInput,
} from '../api/my-profile.types';
import { myProfileQueryKeys } from './use-my-profile';

// PRD 20 §3.3 [인증번호 받기] — issue the SMS code. Cooldown is server-driven; on a 429 the
// FE reads meta.retryAfterSeconds (fallback 60) to display "N초 후 재요청". Returns void (204).
export function useSendPhoneCodeMutation() {
  return useMutation<void, Error, PhoneSendCodeInput>({
    mutationFn: (input) => myProfileApi.sendPhoneCode(input),
  });
}

// PRD 20 §3.3 [전화번호 저장] — verify-commit: the backend checks the 6-digit code AND saves the
// new number in one call, returning the updated profile. ([인증하기] is a client-side format check,
// so there is no separate verify mutation.) On success refetch /my-profile so the GNB + card 1 reflect it.
export function useSavePhoneMutation() {
  const qc = useQueryClient();
  return useMutation<MyProfile, Error, PhoneSaveInput>({
    mutationFn: (input) => myProfileApi.savePhone(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: myProfileQueryKeys.me() });
    },
  });
}

// PRD 20 §3.4 — password change. Session is preserved; only passwordChangedAt updates. Returns void
// (204); refetch /my-profile so card 2's "마지막 변경" header refreshes.
export function useChangePasswordMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, PasswordChangeInput>({
    mutationFn: (input) => myProfileApi.changePassword(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: myProfileQueryKeys.me() });
    },
  });
}
